import { NodeProps } from 'reactflow';
import {
    CreateTaskFormValues,
    CreateTaskNodeData,
} from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { CreateTaskDueAtFormField } from 'assets/js/pages/engage/workflows/components/CreateTaskDueAtFormField';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { DuplicateNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DuplicateNodeMenuItem';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import { getActionNodeHeaderDetails } from 'assets/js/pages/engage/workflows/utils/getActionNodeHeaderDetails';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { TASK_DESCRIPTION_EDITOR_BODY_CLASS } from 'components/tasks/constants';
import { AssignedToFormField } from 'components/tasks/form/AssignedToFormField';
import { editorInitOptions } from 'components/tasks/form/TaskDescriptionEditor';
import { useCrmMembers } from 'components/tasks/utils/useCrmMembers';
import * as Wysiwyg from 'components/wysiwyg';
import { UseFormWysiwyg } from 'components/wysiwyg/UseFormWysiwyg';
import { WorkflowActionTypeEnum } from 'constants/workflows';
import { useCurrentUser } from 'customHooks/useCurrentUser';
import { twJoin } from 'tailwind-merge';

interface CreateTaskActionNodeProps extends NodeProps<CreateTaskNodeData> {}
const CreateTask = ({ id, selected, data: { formData, type } }: CreateTaskActionNodeProps) => {
    const header = getActionNodeHeaderDetails(type);
    const { id: currentUserId } = useCurrentUser();
    const { data: members } = useCrmMembers();

    const currentMemberId = members.find(m => m.id === currentUserId)?.id.toString();

    const methods = Form.useGivebutterForm<CreateTaskFormValues>({
        mode: 'all',
        defaultValues: {
            title: formData?.values.title ?? '',
            description: formData?.values.description ?? '',
            due_at: formData?.values.due_at
                ? {
                      value: formData?.values.due_at?.value,
                      unit: formData?.values.due_at?.unit,
                      skip_weekends: formData?.values.due_at?.skip_weekends ?? false,
                  }
                : null,
            status: formData?.values.status ?? 'todo',
            assigned_to: formData?.values.assigned_to ?? (currentMemberId ? [currentMemberId] : []),
        },
    });

    const {
        control,
        formState: { errors },
        trigger,
        getValues,
        path,
    } = methods;
    const isError = Object.keys(errors).length > 0;

    useSyncFormToNode<CreateTaskFormValues>({ nodeId: id, control });
    useDebouncedAutosave<CreateTaskFormValues>({ methods });
    useTriggerValidationOnDeselection<CreateTaskFormValues>({ trigger, selected });

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    isSelected={selected}
                    isError={isError}
                    id={id}
                    className="w-screen"
                    style={{
                        maxWidth: selected ? 540 : 384,
                    }}
                >
                    <CanvasNode.Container>
                        <CanvasNode.ErrorAlert />
                        <CanvasNode.Header>
                            <CanvasNode.Icon icon={header.icon} className="text-green-500" />
                            <CanvasNode.Title>{header.text}</CanvasNode.Title>
                            <DropdownMenu.Root>
                                <CanvasNode.ActionMenuTrigger />
                                <DropdownMenu.Content align="end">
                                    <DuplicateNodeMenuItem />
                                    <DeleteCanvasNodeMenuItem />
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <CanvasNode.BodySelected>
                                <div className="flex flex-col gap-4 rounded-lg border p-6">
                                    <div className="flex flex-col gap-2">
                                        <Form.Container>
                                            <Form.Label name={path('title')} className="sr-only">
                                                Title
                                            </Form.Label>

                                            <Form.UseFormTextArea
                                                control={control}
                                                name="title"
                                                rows={1}
                                                autoResize
                                                placeholder="Task title"
                                                className={twJoin(
                                                    'max-w-full rounded-none p-0 text-xl font-medium ring-0 placeholder:text-gray-500 focus:ring-0',
                                                    'nopan', // required to allow text selection without panning the viewport
                                                )}
                                                rules={{ required: 'Title is required' }}
                                            />
                                            <Form.UseFormError name={path('title')} />
                                        </Form.Container>
                                        <Form.Container>
                                            <Wysiwyg.Root>
                                                <UseFormWysiwyg
                                                    name="description"
                                                    init={{
                                                        ...editorInitOptions,
                                                        placeholder:
                                                            'Add details about your task here',
                                                    }}
                                                    onInit={(_, editor) => {
                                                        editor.bodyElement?.classList.add(
                                                            TASK_DESCRIPTION_EDITOR_BODY_CLASS,
                                                            'nopan', // required to allow text selection without panning the viewport
                                                        );
                                                    }}
                                                />
                                            </Wysiwyg.Root>
                                        </Form.Container>
                                    </div>
                                    <div className="flex gap-2">
                                        <AssignedToFormField />
                                        <CreateTaskDueAtFormField />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                                    <div className="text-sm font-medium">💡 Tip</div>
                                    <p className="text-xs font-medium text-gray-500">
                                        We will automatically link the relevant contact and other
                                        resources to your task, such as the campaign, transaction,
                                        or ticket.
                                    </p>
                                </div>
                            </CanvasNode.BodySelected>
                            <CanvasNode.BodyUnselected>
                                <CreateTaskActionFormValuesDisplay values={getValues()} />
                            </CanvasNode.BodyUnselected>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

interface CreateTaskActionFormValuesDisplayProps {
    values: CreateTaskFormValues;
    showLabel?: boolean;
}
const CreateTaskActionFormValuesDisplay = ({
    values,
    showLabel = false,
}: CreateTaskActionFormValuesDisplayProps) => {
    const { title } = values;
    const actionHeader = getActionNodeHeaderDetails(WorkflowActionTypeEnum.TASK_CREATE);
    if (!actionHeader) return null;

    return (
        <div className="flex flex-col gap-2">
            {showLabel ? (
                <p className="text-sm font-medium text-gray-900">{actionHeader.text}</p>
            ) : null}
            <p className="text-base text-gray-700">{title}</p>
        </div>
    );
};

export { CreateTask, CreateTaskActionFormValuesDisplay };
export type { CreateTaskActionNodeProps };
